import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '@/stores';
import { homePathForRole } from '@/components/auth';
import { getErrorMessage } from '@/api';
import { Button, Card, FormField, Input } from '@/components/ui';
import { Wordmark } from '@/components/layout/Wordmark';

// ═══════════════════════════════════════════════════════════════════
// VALIDATION SCHEMA
// ═══════════════════════════════════════════════════════════════════

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect target from location state; otherwise the role's home
  const from = (location.state as { from?: { pathname: string } } | null)?.from
    ?.pathname;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    try {
      await login(data.email, data.password);
      const role = useAuthStore.getState().user?.role;
      navigate(from ?? homePathForRole(role), { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-4 py-8">
      <Card variant="section" padding="none" className="w-full max-w-md p-8">
        <header className="mb-8 space-y-3">
          <Wordmark />
          <div className="space-y-1">
            <p className="dc-eyebrow">Admin</p>
            <h1 className="dc-h1 text-[28px] text-ink">Sign in</h1>
          </div>
        </header>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-5"
          noValidate
        >
          {error && (
            <Card
              padding="sm"
              role="alert"
              className="text-sm text-danger ring-danger"
            >
              {error}
            </Card>
          )}

          <FormField label="Email" error={errors.email?.message}>
            <Input
              {...register('email')}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
            />
          </FormField>

          <FormField label="Password" error={errors.password?.message}>
            <div className="relative">
              <Input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                className="pr-12"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-mute hover:text-ink"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </div>
          </FormField>

          <Button
            type="submit"
            className="w-full"
            loading={isLoading}
            loadingLabel="Signing in…"
          >
            Sign in
          </Button>
        </form>
      </Card>
    </div>
  );
}
